import { Container, Sprite } from 'pixi.js';
import type { BlockerView } from '../../types';
import type { StarBlockerModel } from '../../models/blockers/StarBlockerModel';
import type { TileStackView } from '../board/TileStackView';
import { TILE_Z_OFFSET } from '../board/TileView';
import { waitForMs, waitForNextFrame } from '../../../../utils/async-utils';

export class StarBlockerView extends Container implements BlockerView {
  private stackView: TileStackView | null = null;

  private readonly stackSprite = new Sprite().props({
    parent: this,
    texture: 'Blocker_Star_Stack_Top',
    anchor: 0.5,
  });

  private readonly floorSprite = new Sprite().props({
    parent: this,
    texture: 'Blocker_Star_Floor',
    anchor: 0.5,
  });

  constructor(private readonly model: StarBlockerModel) {
    super();
  }

  public getModel(): StarBlockerModel {
    return this.model;
  }

  public setStackView(stackView: TileStackView): void {
    this.stackView = stackView;
    const { model } = stackView;

    this.floorSprite.props({
      parent: this.stackView,
      addBelow: this.stackView.tiles,
    });

    if (!model.hasTiles()) {
      this.stackSprite.props({ scale: 0 });
    }

    const waiting = false;

    let hidden = false;

    async function waitForModelToUnlock(buffer: number): Promise<void> {
      if (waiting) return;

      let currentUnlockedFrame = 0;

      while (currentUnlockedFrame < buffer) {
        await waitForNextFrame();
        if (model.locked) {
          currentUnlockedFrame = 0;
        } else {
          currentUnlockedFrame += 1;
        }
      }
    }

    const hideViewAndWaitForUnlock = async (): Promise<void> => {
      if (hidden) return;

      hidden = true;
      this.stackSprite.tweenTo({ scaleXY: 0 }, 200, 'disappear1', true);
      await waitForModelToUnlock(6);
      this.updateYPosition();
      if (model.hasTiles()) {
        this.stackSprite.tweenTo({ scaleXY: 1 }, 200, 'appear1', true);
      } else {
        this.stackSprite.props({ scale: 0 });
      }
      hidden = false;
    };

    const connections = [
      this.stackView.onTilePreparedForMatchRemoval.connect(() => {
        this.updateYPosition();
        this.stackSprite.props({ scale: 1 });
      }),
      model.onTilesAdded.connect(() => {
        void hideViewAndWaitForUnlock();
      }),
      model.onTilesRemoved.connect(async () => {
        void hideViewAndWaitForUnlock();
      }),
    ];

    this.once('destroyed', () => {
      connections.forEach((connection) => connection.disconnect());
    });

    this.updateYPosition();
  }

  public async animateRemoval(): Promise<void> {
    this.stackSprite.visible = false;
    this.floorSprite.visible = false;
    await waitForMs(100);
    this.destroy();
  }

  public showSprite(): void {
    if (!this.stackView) return;
    const tilesAmount = this.stackView.model.getTiles().length;

    this.updateYPosition();

    this.stackSprite.visible = tilesAmount > 0;
    this.floorSprite.visible = tilesAmount === 0;
  }

  public hideSprite(): void {
    if (!this.stackView) return;

    this.stackSprite.visible = false;
    this.floorSprite.visible = false;
  }

  public destroy(): void {
    this.stackSprite.destroy();
    this.floorSprite.destroy();
    super.destroy({ children: true });
  }

  private readonly updateYPosition = (): void => {
    if (this.destroyed) return;
    if (!this.stackView) return;
    if (this.stackView.destroyed) return;

    const tilesAmount = this.stackView.model.getTiles().length;

    const y = tilesAmount * -TILE_Z_OFFSET;

    this.props({ y, visible: tilesAmount > 0 });
  };
}
